import { h } from "preact";
import { StateUpdater, useEffect, useState } from "preact/hooks";
import { resolvePath } from "../api";
import { ManufacturingConfiguration, Product } from "../api/product-api";
import { generate, labelsMetadataArray, slaveInfo, slavePrint } from "../api/label-creator";
import { egzotechAgentManager, route } from "../App";
import { useForceUpdate } from "../hooks";
import { Alert } from "./Alert";
import { fillLabelFields, fillLabelFieldsAsync, generateExampleFieldsForLabel, group } from "../utils";
import { filterDefaultLabelsForRevision } from "../data/filterDefaultLabelsForRevision";

function getTapeWidthAndType(metadata: { [key: string]: string }) {
    const tapeTypeRegex = /(.*)ink\s+on(.*)tape.*?([a-zA-Z0-9 ]+)/m;
    const tapeWidth = parseInt(metadata["tape-width"] ?? "", 10);
    const tapeType = metadata["tape-type"];

    if (!tapeType || isNaN(tapeWidth) || !isFinite(tapeWidth) || tapeWidth < 0) {
        return null;
    }

    const result = tapeTypeRegex.exec(tapeType);

    if (!result) {
        return null;
    }

    return `${tapeWidth} ${result[1].trim().replace(/\s+/gm, "_")}_on_${result[2].trim().replace(/\s+/gm, "_")} ${result[3].trim().replace(/\s+/gm, "_")}`;
}

export interface SerialNumberData {
    preview: string;
    id: string;
    name: string;
    description?: string;
    template?: string;
    type?: string;
    innerType?: "list";
    inner?: SerialNumberData[];
    metadata?: { [key: string]: string };
}

async function loadMetadata(serialNumbers: ManufacturingConfiguration[]) {
    // const serials = serialNumbers.filter(s => !s.inner);
    // const inners = serialNumbers.filter(s => s.inner);
    const templates = serialNumbers.map(
        s => filterDefaultLabelsForRevision(s.productType.currentProductTypeRevision!)[0].label.path,
    );

    const result = await labelsMetadataArray(templates);

    for (let i = 0; i < templates.length; i++) {
        const revision = serialNumbers[i].productType.currentProductTypeRevision;
        const availableDefaultLabels = revision ? filterDefaultLabelsForRevision(revision) : null;

        serialNumbers[i].metadata = {
            ...result[i],
            ...(availableDefaultLabels ? availableDefaultLabels[0].label.metadata?.metadata : {}),
        };
    }

    // for (let i = 0; i < inners.length; i++) {
    //     await loadMetadata(inners[i].inner as any);
    // }
}

function generateLabelPreviewForConfig(config: ManufacturingConfiguration) {
    const productType = config.productType;
    const revision = config.productType.currentProductTypeRevision!;
    const availableDefaultLabels = filterDefaultLabelsForRevision(revision).map(v => v.label);
    const label = availableDefaultLabels[0];

    return generate(
        label.path!,
        {
            ...generateExampleFieldsForLabel(label.metadata?.metadata ?? {}),
            ...fillLabelFields(
                label.metadata?.metadata ?? {},
                {
                    revision: {
                        ...revision,
                        productType: productType,
                    },
                },
                {},
            ),
        },
        label.metadata?.metadata,
        undefined,
        false,
    );
}

function AccessoryLabelTile({
    d,
    setLabelsQueue,
}: {
    d: ManufacturingConfiguration;
    setLabelsQueue: StateUpdater<ManufacturingConfiguration[]>;
}) {
    const availableDefaultLabels = filterDefaultLabelsForRevision(d.productType.currentProductTypeRevision!).map(
        v => v.label,
    );

    return (
        <div class="label-item paper-2">
            <div class="preview">
                <img src={resolvePath(d.preview)} />
            </div>
            <div class="content">
                <div class="name">{d.productType.name}</div>
                {/* <div class="description">{d.description}</div> */}
                {availableDefaultLabels.length > 1 ? (
                    <div class="tape-type">Wiele rewizji lub naklejek</div>
                ) : (
                    <div class="tape-type">{d.metadata ? d.metadata["tape-type"] : null}</div>
                )}
                <div class="queue-tape-width">
                    <button
                        type="button"
                        class="flat icon r26 borderless"
                        data-tooltip="Wydrukuj..."
                        onClick={() => {
                            route(`/print-serial-number/${d.productType.name}`);
                        }}
                    >
                        print
                    </button>
                    {availableDefaultLabels.length > 1 ? (
                        <div
                            class="tooltip"
                            data-tooltip="Ten produkt posiada wiele naklejek i nie można drukować go ciągiem"
                        >
                            <button
                                type="button"
                                class="flat icon r26 borderless disabled"
                                style="color: var(--primary-color)"
                            >
                                queue
                            </button>
                        </div>
                    ) : (
                        [
                            <button
                                type="button"
                                class="flat icon r26 borderless"
                                data-tooltip="Dodaj bieżącą rewizje do kolejki"
                                onClick={() => {
                                    setLabelsQueue(prev => prev.concat([d]));
                                }}
                            >
                                queue
                            </button>,
                            <div class="fill" />,
                            <div class="tape-width">{d.metadata ? d.metadata["tape-width"] : null}</div>,
                        ]
                    )}
                </div>
            </div>
        </div>
    );
}

export function AccessoriesPrinting(props: {
    manufacturingConfigurations: ManufacturingConfiguration[];
    serialType: "serial-8-base32" | "serial-6-base16";
    generateSerialNumber: (type: string) => Promise<Product>;
    hiddenConfigurations?: string[];
}) {
    const [innerVisible, setInnerVisible] = useState(false);
    const [innerData, setInnerData] = useState(null as null | ManufacturingConfiguration[]);
    const [innerDataName, setInnerDataName] = useState(null as null | string);
    const [innerDataType, setInnerDataType] = useState(null as null | string);
    const [labelsQueue, setLabelsQueue] = useState([] as ManufacturingConfiguration[]);
    const [groupedLabelsQueue, setGroupedLabelsQueue] = useState({} as { [key: string]: ManufacturingConfiguration[] });
    const [printingState, setPrintingState] = useState("");
    const [printingStateInfo, setPrintingStateInfo] = useState("");
    const [tapeImg, setTapeImg] = useState("");
    const [eachLabelCopies, setEachLabelCopies] = useState(1);
    const forceUpdate = useForceUpdate();

    const groupedConfigurations = group(
        props.manufacturingConfigurations.filter(v =>
            props.hiddenConfigurations ? !props.hiddenConfigurations.includes(v.productType.name) : true,
        ),
        v => (v.group ? v.group.id : 0).toString(),
        v => v.group,
    );

    const noGroupedConfigurationsIndex = groupedConfigurations.findIndex(v => v.key === null);
    const noGroupedConfigurations = groupedConfigurations[noGroupedConfigurationsIndex];

    if (noGroupedConfigurationsIndex >= 0) {
        groupedConfigurations.splice(noGroupedConfigurationsIndex, 1);
    }

    useEffect(() => {
        if (props.manufacturingConfigurations.length === 0) {
            return;
        }

        loadMetadata(props.manufacturingConfigurations).then(() => {
            forceUpdate();
        });
    }, [true]);

    useEffect(() => {
        const groups = {} as { [key: string]: ManufacturingConfiguration[] };

        let commonTapeType: string | undefined | null;

        setTapeImg("");

        for (let i = 0; i < labelsQueue.length; i++) {
            const item = labelsQueue[i];

            if (!groups[item.productType.id]) {
                groups[item.productType.id] = [];
            }

            if (item.metadata) {
                const tapeType = getTapeWidthAndType(item.metadata);

                if (commonTapeType === undefined) {
                    commonTapeType = tapeType;
                }

                if (tapeType === null || tapeType !== commonTapeType) {
                    commonTapeType = null;
                }
            }

            groups[item.productType.id].push(labelsQueue[i]);
        }

        labelsQueue.splice(0);

        for (const prop in groups) {
            for (let i = 0; i < groups[prop].length; i++) {
                labelsQueue.push(groups[prop][i]);
            }
        }

        if (commonTapeType) {
            setTapeImg(`${resolvePath("images/tapes/")}${commonTapeType}.jpg`);
        } else if (commonTapeType === null) {
            setTapeImg("error");
        }

        setGroupedLabelsQueue(groups);
    }, [labelsQueue]);

    return (
        <div class="accessories-printing-component">
            <div class="queue-visualization paper">
                <h1>Drukowanie ciągiem</h1>
                <div class="labels-queue-tape">
                    <div class="labels-queue">
                        {Object.entries(groupedLabelsQueue).map(([key, g]) => (
                            <div class="group">
                                <svg>
                                    <text
                                        style="font-size:3mm;line-height:1.25;font-family:'Roboto Mono', monospace;letter-spacing:-0.264583px;fill:#ffffff;stroke:#ffffff;stroke-width:0.2px"
                                        x="-39mm"
                                        y="6mm"
                                        transform="rotate(-90)"
                                    >
                                        {g.length > 0 ? g[0].productType.name : null}
                                    </text>
                                </svg>
                                {g.length > 4 ? (
                                    <div class="entry-container">
                                        <div class="actions" style="background: rgba(100, 100, 100, 0.9)">
                                            <button
                                                class="icon flat borderless"
                                                onClick={() => {
                                                    labelsQueue.splice(labelsQueue.indexOf(g[g.length - 1]), 1);
                                                    setLabelsQueue(labelsQueue.slice());
                                                }}
                                            >
                                                delete
                                            </button>
                                        </div>
                                        <div
                                            class="entry"
                                            style={{ height: parseInt(g[g.length - 1].metadata!["tape-width"]) + "mm" }}
                                        >
                                            <div class="margin" />
                                            <img
                                                src={generateLabelPreviewForConfig(g[g.length - 1])}
                                                style="height: 100%"
                                            />
                                            <div class="margin" />
                                        </div>
                                        <div style="margin-left: 5px">x{g.length}</div>
                                    </div>
                                ) : (
                                    g.map(entry => (
                                        <div class="entry-container">
                                            <div class="actions" style="background: rgba(100, 100, 100, 0.9)">
                                                <button
                                                    class="icon flat borderless"
                                                    onClick={() => {
                                                        labelsQueue.splice(labelsQueue.indexOf(entry), 1);
                                                        setLabelsQueue(labelsQueue.slice());
                                                    }}
                                                >
                                                    delete
                                                </button>
                                            </div>
                                            <div
                                                class="entry"
                                                style={{ height: parseInt(entry.metadata!["tape-width"]) + "mm" }}
                                            >
                                                <div class="margin" />
                                                <img
                                                    src={generateLabelPreviewForConfig(g[g.length - 1])}
                                                    style="height: 100%"
                                                />
                                                <div class="margin" />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ))}
                    </div>
                    <div class="tape">
                        <h2>Rodzaj taśmy</h2>
                        {tapeImg === "error" ? (
                            <Alert type="error" title="Błąd">
                                Wybrano naklejki z różnymi rodzajami taśm
                            </Alert>
                        ) : tapeImg ? (
                            <img src={tapeImg} height={128} />
                        ) : (
                            <Alert type="info" title="Informacja">
                                Nie wybrano naklejek, brak taśmy do wyświetlenia
                            </Alert>
                        )}
                    </div>
                </div>
                <div style="margin-top: 10px">
                    <label
                        class="tooltip tooltip-info tooltip-right"
                        data-tooltip={
                            "Każda kopia będzie miała taki sam numer seryjny. Przykład: W przypadku wybrania 3 naklejek i 2 kopii zostanie wydrukowane 6 naklejek."
                        }
                    >
                        Ilość kopii każdej naklejki:{" "}
                        <input
                            type="number"
                            style="margin-left: 10px; width: 60px;"
                            value={eachLabelCopies}
                            onChange={e => setEachLabelCopies(parseInt(e.currentTarget.value, 10))}
                        />
                    </label>
                </div>
                <div class="printing">
                    <button
                        class={!tapeImg || tapeImg === "error" ? "disabled" : ""}
                        onClick={async () => {
                            setPrintingState("printing");

                            try {
                                const info = await slaveInfo(egzotechAgentManager.getAgentWithModule("label-printer")!);

                                if (!info || !info.portName) {
                                    setPrintingState("no-printer");
                                    return;
                                }

                                const groupedQueueKeys = Object.keys(groupedLabelsQueue);

                                if (
                                    groupedQueueKeys.length === 0 ||
                                    Object.values(groupedLabelsQueue).every(v => v.length === 0)
                                ) {
                                    setPrintingState("queue-empty");
                                    return;
                                }

                                if (
                                    Object.values(groupedLabelsQueue).some(v =>
                                        v.some(
                                            item =>
                                                !item.metadata ||
                                                info.mediaWidth !== parseInt(item.metadata["tape-width"]),
                                        ),
                                    )
                                ) {
                                    setPrintingState("bad-tape-width");

                                    const metadata = groupedLabelsQueue[groupedQueueKeys[0]][0].metadata;

                                    setPrintingStateInfo(
                                        `Oczekiwana: ${metadata ? metadata["tape-width"] : "B/D"}, włożona: ${info.mediaWidth}mm`,
                                    );
                                    return;
                                }

                                const flattenedQueue = [] as (
                                    | ManufacturingConfiguration
                                    | {
                                          template: Blob;
                                      }
                                )[];

                                let useHighQuality = false;

                                Object.values(groupedLabelsQueue).forEach(v => {
                                    const name = v[0].productType.name;

                                    const hasHighQuality = v[0].metadata
                                        ? v[0].metadata!["high-quality"].trim().toLowerCase() === "true"
                                        : false;

                                    if (hasHighQuality) {
                                        useHighQuality = true;
                                    }

                                    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

                                    svg.setAttribute("width", "28");
                                    svg.setAttribute("height", "128");
                                    svg.setAttribute("viewBox", "0 0 7.4083332 33.866668");

                                    const fontSize = hasHighQuality ? "5px" : "7px";

                                    svg.innerHTML =
                                        `<text xml:space="preserve" style="font-style:normal;font-weight:normal;font-size:4.23333px;line-height:1.25;font-family:sans-serif;letter-spacing:-0.264583px;fill:#000000;fill-opacity:1;stroke:none;stroke-width:0.885751" id="text835" transform="rotate(-90)">` +
                                        `<tspan id="tspan833" x="-33.23053" y="5.01406" style="font-style:normal;font-variant:normal;font-weight:bold;font-stretch:normal;font-size:${fontSize};font-family:'Roboto Mono';-inkscape-font-specification:'Roboto Mono';stroke-width:0.885751">${name}</tspan>` +
                                        `</text>`;

                                    const serializer = new XMLSerializer();
                                    const svgData = serializer.serializeToString(svg);

                                    flattenedQueue.push({
                                        template: new Blob([svgData], { type: "image/svg+xml" }),
                                    });
                                    flattenedQueue.push(...v);
                                });

                                for (let i = 0; i < flattenedQueue.length; i++) {
                                    const item = flattenedQueue[i];

                                    if ("template" in item) {
                                        const overrideMetadata: { [key: string]: string } = {};

                                        if (useHighQuality) {
                                            overrideMetadata["high-quality"] = "true";
                                        } else {
                                            overrideMetadata["high-quality"] = "false";
                                        }

                                        await slavePrint(
                                            egzotechAgentManager.getAgentWithModule("label-printer")!,
                                            item.template!,
                                            {},
                                            {
                                                copies: i === flattenedQueue.length - 1 ? 1 : i === 0 ? -2 : -1,
                                                noAutoCut: true,
                                            },
                                            overrideMetadata,
                                        );

                                        continue;
                                    }

                                    // const typeData = types[item.id];
                                    const fields: { [key: string]: any } = {};
                                    const revision = item.productType.currentProductTypeRevision;

                                    await fillLabelFieldsAsync(
                                        item.metadata!,
                                        {
                                            serialNumber: async () => {
                                                const result = await props.generateSerialNumber(item.productType.name);

                                                if (!result?.serialNumber) {
                                                    throw new Error("Missing serial number");
                                                }

                                                return result.serialNumber;
                                            },
                                            revision,
                                        },
                                        fields,
                                    );

                                    const availableDefaultLabels = filterDefaultLabelsForRevision(
                                        item.productType.currentProductTypeRevision!,
                                    ).map(v => v.label);

                                    for (let j = 0; j < eachLabelCopies; j++) {
                                        await slavePrint(
                                            egzotechAgentManager.getAgentWithModule("label-printer")!,
                                            availableDefaultLabels[0].path!,
                                            fields,
                                            {
                                                copies:
                                                    i === flattenedQueue.length - 1 && j === eachLabelCopies - 1
                                                        ? 1
                                                        : i === 0 && j === 0
                                                          ? -2
                                                          : -1,
                                                noAutoCut: true,
                                            },
                                            availableDefaultLabels[0].metadata?.metadata,
                                        );
                                    }
                                }
                            } catch (ex) {
                                console.error(ex);
                                setPrintingState("error");
                                return;
                            }

                            setPrintingState("done");
                        }}
                    >
                        Wydrukuj
                    </button>
                    <button
                        class="error"
                        onClick={() => {
                            setLabelsQueue([]);
                            setPrintingState("");
                        }}
                    >
                        Wyczyść
                    </button>
                    {printingState === "printing" ? <div class="loader" /> : null}
                    {printingState === "printing" ? (
                        <Alert type="info">Drukowanie...</Alert>
                    ) : printingState === "done" ? (
                        <Alert type="success">Drukowanie zakończone</Alert>
                    ) : printingState === "error" ? (
                        <Alert type="error">Wystąpił błąd podczas drukowania</Alert>
                    ) : printingState === "no-printer" ? (
                        <Alert type="error">Brak komunikacji z drukarką</Alert>
                    ) : printingState === "queue-empty" ? (
                        <Alert type="error">Brak naklejek do wydrukowania</Alert>
                    ) : printingState === "bad-tape-width" ? (
                        <Alert type="error">Błędna szerokość taśmy w drukarce ({printingStateInfo})</Alert>
                    ) : null}
                </div>
            </div>
            <div class="labels-container">
                <div class="labels paper">
                    <div class="labels-list">
                        {noGroupedConfigurations &&
                            noGroupedConfigurations.items.map(d => (
                                <AccessoryLabelTile d={d} setLabelsQueue={setLabelsQueue} />
                            ))}
                        {groupedConfigurations.map(d => (
                            <div class="label-item paper-2">
                                <div class="preview">
                                    <img src={resolvePath(d.key!.preview ?? "")} />
                                </div>
                                <div class="content">
                                    <div class="name">{d.key!.name}</div>
                                    {/* <div class="description">{d.description}</div> */}
                                    <div class="queue-tape-width">
                                        <button
                                            type="button"
                                            class="flat icon r26 borderless"
                                            data-tooltip="Pokaż wszystkie możliwości..."
                                            onClick={() => {
                                                setInnerVisible(true);
                                                setInnerData(d.items);
                                                setInnerDataName(d.key!.name);
                                                console.log(d);
                                                setInnerDataType(d.key!.type ?? null);
                                            }}
                                        >
                                            open_in_new
                                        </button>
                                        <div class="fill" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div>
                        <input
                            type="checkbox"
                            class="popup-visible"
                            checked={innerVisible}
                            onChange={e => setInnerVisible(e.currentTarget.checked)}
                            id="popup-visible-controller-stella-bio-serial-number"
                        />
                        <div class="popup">
                            <label class="background" for="popup-visible-controller-stella-bio-serial-number"></label>
                            <div class="content" style="display: flex; flex-direction: column; padding: 10px;">
                                <div style="display: flex; align-items: center;">
                                    <h1 style="margin-bottom: 0">{innerDataName}</h1>
                                    <div style="flex: 1 1" />
                                    <label
                                        for="popup-visible-controller-stella-bio-serial-number"
                                        class="button icon flat"
                                        style="flex-shrink: 0; align-self: flex-end;"
                                    >
                                        close
                                    </label>
                                </div>
                                <div class="labels-list-container" style="margin-top: 10px; align-self: center;">
                                    <div class={innerDataType === "list" ? "labels-list single-column" : "labels-list"}>
                                        {innerData
                                            ? innerData.map(d => (
                                                  <AccessoryLabelTile d={d} setLabelsQueue={setLabelsQueue} />
                                              ))
                                            : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
